import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer';
import PostProcessing from './effects/PostProcessing';
import WireframeScene from './wireframe/WireframeScene';
import IntroAnimation from './animations/IntroAnimation';
import P5Sketch from '../scanner/P5Sketch';
import ResetCameraButton from './controls/ResetCameraButton';

// Camera controls wrapper component
const CameraController = ({ onReady, controlsRef }) => {
  const { camera, scene } = useThree();
  
  useEffect(() => {
    if (camera && controlsRef.current && scene) {
      onReady({
        camera,
        controls: controlsRef,  // Pass the ref itself
        scene
      });
    }
  }, [camera, controlsRef, scene, onReady]);
  
  return null;
};

const RealityStackViewer = ({ onBack }) => {
  const [introComplete, setIntroComplete] = useState(false);
  const [showP5Sketch, setShowP5Sketch] = useState(false);
  const [selectedPlane, setSelectedPlane] = useState(null);
  const [cameraState, setCameraState] = useState(null);
  const labelRenderer = useRef();
  const controlsRef = useRef();
  
  const handleCameraReady = useCallback((state) => {
    setCameraState(state);
  }, []);

  useEffect(() => {
    if (cameraState) {
      // Initialize CSS2DRenderer
      const renderer = new CSS2DRenderer();
      renderer.domElement.style.position = 'absolute';
      renderer.domElement.style.top = '0px';
      renderer.domElement.style.left = '0px';
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      renderer.domElement.style.pointerEvents = 'none';
      renderer.domElement.style.zIndex = '1';
      const container = document.getElementById('canvas-container');
      if (container) {
        container.appendChild(renderer.domElement);
        labelRenderer.current = renderer;

        // Set up render loop
        const animate = () => {
          if (labelRenderer.current && cameraState.scene && cameraState.camera) {
            labelRenderer.current.render(cameraState.scene, cameraState.camera);
          }
          requestAnimationFrame(animate);
        };
        animate();
    
        return () => {
          if (labelRenderer.current && container.contains(labelRenderer.current.domElement)) {
            container.removeChild(labelRenderer.current.domElement);
          }
        };
      } else {
        console.error('Canvas container not found');
        return () => {};
      }
    }
  }, [cameraState]);

  const handlePlaneClick = (index) => {
    setSelectedPlane(index);
    setShowP5Sketch(true);
  };

  return (
    <>
      <div
        id="canvas-container"
        style={{
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          background: '#001100',
          zIndex: 1,
          overflow: 'hidden'
        }}
      >
        <button
          onClick={onBack}
          role="button"
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            zIndex: 1000,
            background: 'rgba(0, 255, 0, 0.2)',
            border: '2px solid #0f0',
            color: '#0f0',
            padding: '8px 16px',
            cursor: 'pointer',
            fontFamily: 'leaflings',
            fontSize: '14px',
            textTransform: 'uppercase',
            boxShadow: '0 0 10px rgba(0, 255, 0, 0.3)',
            transition: 'all 0.3s ease',
            textShadow: '0 0 5px #0f0'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(0, 255, 0, 0.3)';
            e.target.style.boxShadow = '0 0 15px rgba(0, 255, 0, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(0, 255, 0, 0.2)';
            e.target.style.boxShadow = '0 0 10px rgba(0, 255, 0, 0.3)';
          }}
        >
          ← Back
        </button>
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <Canvas
            camera={{ position: [300, 300, 300], fov: 45 }}
            style={{ position: 'absolute', width: '100%', height: '100%', pointerEvents: 'auto' }}
          >
            <CameraController onReady={handleCameraReady} controlsRef={controlsRef} />
            <color attach="background" args={['#001100']} />
            <WireframeScene onPlaneClick={handlePlaneClick} />
            {!introComplete && (
              <IntroAnimation onComplete={() => setIntroComplete(true)} />
            )}
            <OrbitControls
              makeDefault
              enableZoom={true}
              enablePan={true}
              rotateSpeed={0.5}
              minPolarAngle={Math.PI / 8}
              maxPolarAngle={Math.PI * 0.75}
              ref={controlsRef}
            />
            <PostProcessing />
          </Canvas>
          {introComplete && cameraState && (
            <ResetCameraButton state={cameraState} />
          )}
        </div>
      </div>
      {showP5Sketch && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1000,
          background: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <P5Sketch
            onClose={() => setShowP5Sketch(false)}
            selectedLayer={selectedPlane}
            selectedSeed={`REALITY-${selectedPlane}`}
          />
        </div>
      )}
    </>
  );
};

export default RealityStackViewer;